import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DataCacheProvider } from "./context/DataCacheContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import { UpgradePromptProvider } from "./components/subscription/UpgradePromptContext";
import { useBudgetPeriodEndedCheck } from "./pages/BudgetPeriodEndedPage";
import { useTrialTransitionCheck } from "./pages/TrialTransition";
import TrialEndingBanner from "./components/TrialEndingBanner";
import BetaBanner from "./components/BetaBanner";
import ErrorBoundary from "./components/ErrorBoundary";
import { resolveLandingPath } from "./utils/landingPage";
import { toast } from "sonner";
const TrialTransitionPage = lazy(() => import("./pages/TrialTransition"));
const UpgradePromptDemo = lazy(() => import("./components/subscription/UpgradePrompt.demo"));
const BudgetPeriodEndedPage = lazy(() => import("./pages/BudgetPeriodEndedPage"));

// Pages (lazy loaded + prefetched for fast navigation)
const LandingPage = lazy(() => import("./pages/LandingPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const DashboardPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/DashboardPage"));
const StocksPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/StocksPage"));
const RealEstatePage = lazy(() => import(/* webpackPrefetch: true */ "./pages/RealEstatePage"));
const CryptoPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/CryptoPage"));
const BondsPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/BondsPage"));
const ExpensesPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/ExpensesPage"));
const LoansPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/LoansPage"));
const GoalsPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/GoalsPage"));
const SubscriptionPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/SubscriptionPage"));
const SettingsPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/SettingsPage"));
const CreditSimulatorPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/CreditSimulatorPage"));
const MarketsPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/MarketsPage"));
const CollectionPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/CollectionPage"));
const RevenusPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/RevenusPage"));
const EpargnePage = lazy(() => import(/* webpackPrefetch: true */ "./pages/EpargnePage"));
const LiquiditesPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/LiquiditesPage"));
const BudgetPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/BudgetPage"));
const FamilyPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/FamilyPage"));
const FamilyBudgetPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/FamilyBudgetPage"));
const FamilyCommonCategoryPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/FamilyCommonCategoryPage"));
const FamilyRealEstatePage = lazy(() => import(/* webpackPrefetch: true */ "./pages/FamilyRealEstatePage"));
const FamilyCollectionPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/FamilyCollectionPage"));
const FamilyLoansPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/FamilyLoansPage"));
const FamilyGoalsPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/FamilyGoalsPage"));
const CompoundInterestPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/CompoundInterestPage"));
const DocumentsPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/DocumentsPage"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const PricingPage = lazy(() => import("./pages/Pricing"));
const SubscriptionSettingsPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/SubscriptionSettingsPage"));
const SuccessPage = lazy(() => import("./pages/Success"));
const CancelPage = lazy(() => import("./pages/Cancel"));
// Landing fallback shown when the user's preference targets a family
// page but they don't have a household yet (or no longer have one).
const CreateHouseholdPrompt = lazy(() => import("./pages/CreateHouseholdPrompt"));
// Public legal pages — required by Belgian law to remain accessible
// without authentication. All three pages ship with full content
// matching the brand palette (navy + gold) and structure.
const MentionsLegales          = lazy(() => import("./pages/MentionsLegales"));
const PolitiqueConfidentialite = lazy(() => import("./pages/PolitiqueConfidentialite"));
const ConditionsUtilisation    = lazy(() => import("./pages/ConditionsUtilisation"));
// PWA — shown when the SW serves the React app but the network fails.
const OfflinePage = lazy(() => import("./pages/OfflinePage"));
import PwaInstallBanner from "./components/PwaInstallBanner";
import { usePwaLifecycle } from "./hooks/usePwaLifecycle";

const PageLoader = () => (
  <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    if (user?.onboarding_completed === false) {
      return <Navigate to="/onboarding" replace />;
    }
    // Honor user.default_landing_page, with a graceful fallback +
    // toast if the chosen page is no longer accessible (e.g. user
    // was in a household, then left → preferred 'family_patrimony'
    // would land on a page they can't use).
    const dest = resolveLandingPath(user, (reason) => {
      if (reason === 'no_family_access') {
        toast.info("Tu n'as pas accès à la partie Famille — invite ton conjoint ou passe au plan Famille pour y accéder");
      }
    });
    return <Navigate to={dest} replace />;
  }

  return children;
};

// Root route — `/` redirects authenticated users to their preferred
// landing page (honoring user.default_landing_page); otherwise serves
// the marketing LandingPage. This complements PublicRoute, which only
// covers /login and /register: typing the bare domain in the address
// bar previously left the marketing page rendered for logged-in users
// regardless of their preference.
const RootRoute = ({ forceLang } = {}) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    if (user?.onboarding_completed === false) {
      return <Navigate to="/onboarding" replace />;
    }
    const dest = resolveLandingPath(user, (reason) => {
      if (reason === 'no_family_access') {
        toast.info("Tu n'as pas accès à la partie Famille — invite ton conjoint ou passe au plan Famille pour y accéder");
      }
    });
    return <Navigate to={dest} replace />;
  }

  // forceLang is set by the /en route so the page renders in English
  // regardless of the localStorage / navigator preference.
  return <LandingPage forceLang={forceLang} />;
};

// Main Router
const AppRouter = () => {
  const location = useLocation();
  const { user } = useAuth();

  useBudgetPeriodEndedCheck(user);
  useTrialTransitionCheck(user);
  usePwaLifecycle();

  // Handle Google OAuth callback - check URL fragment for session_id
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <BetaBanner />
      {/* Trial-ending nag is mute in beta — it would push users towards
          a /pricing page that itself is replaced by a beta notice. */}
      {!user?.beta_mode && <TrialEndingBanner user={user} />}
      <PwaInstallBanner />
    <Suspense fallback={<PageLoader />}>
    {/* page-enter: CSS-only mobile transition. Keyed by pathname so the
        wrapper remounts on every navigation, replaying the entrance
        animation. No-op on desktop and when prefers-reduced-motion is set. */}
    <div key={location.pathname} className="page-enter">
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<RootRoute />} />
      {/* English landing — same routing logic as / but pins the
          LandingPage language to English. Authenticated users still
          get redirected to their dashboard. */}
      <Route path="/en" element={<RootRoute forceLang="en" />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/simulator" element={<CreditSimulatorPage />} />
      <Route path="/pricing"   element={<PricingPage />} />
      <Route path="/success"   element={<SuccessPage />} />
      <Route path="/cancel"    element={<CancelPage />} />

      {/* PWA offline route — always reachable, no auth */}
      <Route path="/offline" element={<OfflinePage />} />

      {/* Public legal pages — must stay reachable without auth */}
      <Route path="/mentions-legales"           element={<MentionsLegales />} />
      <Route path="/politique-confidentialite"  element={<PolitiqueConfidentialite />} />
      <Route path="/conditions-utilisation"     element={<ConditionsUtilisation />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/stocks" element={<ProtectedRoute><StocksPage /></ProtectedRoute>} />
      <Route path="/real-estate" element={<ProtectedRoute><RealEstatePage /></ProtectedRoute>} />
      <Route path="/crypto" element={<ProtectedRoute><CryptoPage /></ProtectedRoute>} />
      <Route path="/bonds" element={<ProtectedRoute><BondsPage /></ProtectedRoute>} />
      <Route path="/expenses" element={<ProtectedRoute><ExpensesPage /></ProtectedRoute>} />
      <Route path="/loans" element={<ProtectedRoute><LoansPage /></ProtectedRoute>} />
      <Route path="/goals" element={<ProtectedRoute><GoalsPage /></ProtectedRoute>} />
      <Route path="/collection" element={<ProtectedRoute><CollectionPage /></ProtectedRoute>} />
      <Route path="/markets" element={<ProtectedRoute><MarketsPage /></ProtectedRoute>} />
      <Route path="/revenus" element={<ProtectedRoute><RevenusPage /></ProtectedRoute>} />
      <Route path="/epargne" element={<ProtectedRoute><EpargnePage /></ProtectedRoute>} />
      <Route path="/liquidites" element={<ProtectedRoute><LiquiditesPage /></ProtectedRoute>} />
      <Route path="/budget" element={<ProtectedRoute><BudgetPage /></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/settings/subscription" element={<ProtectedRoute><SubscriptionSettingsPage /></ProtectedRoute>} />
      <Route path="/family" element={<ProtectedRoute><ErrorBoundary><FamilyPage /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/family/budget" element={<ProtectedRoute><ErrorBoundary><FamilyBudgetPage /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/family/patrimoine-commun/real_estate" element={<ProtectedRoute><ErrorBoundary><FamilyRealEstatePage /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/family/patrimoine-commun/collection" element={<ProtectedRoute><ErrorBoundary><FamilyCollectionPage /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/family/patrimoine-commun/loans" element={<ProtectedRoute><ErrorBoundary><FamilyLoansPage /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/family/patrimoine-commun/:category" element={<ProtectedRoute><ErrorBoundary><FamilyCommonCategoryPage /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/family/goals" element={<ProtectedRoute><ErrorBoundary><FamilyGoalsPage /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/family/create-household-prompt" element={<ProtectedRoute><CreateHouseholdPrompt /></ProtectedRoute>} />
      <Route path="/compound-interest" element={<ProtectedRoute><CompoundInterestPage /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />

      {/* Subscription / paywall routes */}
      <Route path="/budget-period-ended" element={<ProtectedRoute><BudgetPeriodEndedPage /></ProtectedRoute>} />
      <Route path="/trial-transition" element={<ProtectedRoute><TrialTransitionPage /></ProtectedRoute>} />

      {/* Dev routes — no auth required */}
      <Route path="/dev/upgrade-demo" element={<UpgradePromptDemo />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </div>
    </Suspense>
    </>
  );
};

const ThemedToaster = () => {
  const { theme } = useTheme();
  return (
    <Toaster
      position="top-right"
      toastOptions={{
        style: theme === 'light'
          ? { background: '#ffffff', border: '1px solid #e4e4e7', color: '#18181b' }
          : { background: '#18181B', border: '1px solid #27272A', color: '#FAFAFA' }
      }}
    />
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <DataCacheProvider>
            <UpgradePromptProvider>
              <AppRouter />
              <ThemedToaster />
            </UpgradePromptProvider>
          </DataCacheProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
